/**
 * Error envelope + canonical error codes + ApiError class.
 * Contract: docs/plans/00-foundation-architecture.md §6.4 (LOCKED).
 *
 * Every Edge Function returns errors as the `ErrorEnvelope` shape. The `code`
 * is a stable machine code; `message` is a localizable key resolvable via
 * @gym/i18n (errors.* namespace). `retriable` hints offline/retry logic.
 */

/**
 * Canonical error-code union (docs/plans/00 §6.4). Non-exhaustive at the spec
 * level but this is the locked baseline; new codes require a doc-00 update.
 */
export const ERROR_CODES = [
  'unauthorized',
  'forbidden',
  'tenant_mismatch',
  'not_found',
  'validation_failed',
  'idempotency_key_conflict',
  'idempotency_in_progress',
  'membership_expired',
  'membership_inactive',
  'session_pack_empty',
  'payment_failed',
  'fiscal_unavailable',
  'fiscal_failed_retriable',
  'z_report_already_run',
  'rate_limited',
  'conflict',
  'internal_error',
  // Booking subsystem (plan 07) — added in build run 11.
  'booking_window_not_open',
  'booking_window_closed',
  'booking_capacity_full',
  'booking_already_exists',
  'booking_cancel_cutoff_passed',
  'session_cancelled',
  'waitlist_not_found',
  'pt_session_conflict',
  'pt_pack_required',
  // Payments integration (plan 11) — added in build run 15.
  'invoice_not_issued',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

/** The body shape returned on every Edge Function error (doc 00 §6.4). */
export interface ErrorEnvelope {
  error: {
    /** Stable machine code, e.g. "membership_expired". */
    code: ErrorCode;
    /** Human-readable, localizable key resolvable via @gym/i18n. */
    message: string;
    /** Optional structured context. */
    details?: Record<string, unknown>;
    /** For log correlation. */
    request_id: string;
    /** Retry hint (esp. for fiscal/offline logic). */
    retriable: boolean;
  };
}

export interface ApiErrorOptions {
  message?: string;
  details?: Record<string, unknown>;
  requestId?: string;
  retriable?: boolean;
  /** Override the HTTP status; otherwise derived from the code. */
  httpStatus?: number;
  cause?: unknown;
}

/** Default HTTP status per error code (doc 00 §6.4 "HTTP status reflects the class"). */
const DEFAULT_HTTP_STATUS: Record<ErrorCode, number> = {
  unauthorized: 401,
  forbidden: 403,
  tenant_mismatch: 403,
  not_found: 404,
  validation_failed: 422,
  idempotency_key_conflict: 409,
  idempotency_in_progress: 409,
  membership_expired: 409,
  membership_inactive: 409,
  session_pack_empty: 409,
  payment_failed: 402,
  fiscal_unavailable: 503,
  fiscal_failed_retriable: 503,
  z_report_already_run: 409,
  rate_limited: 429,
  conflict: 409,
  internal_error: 500,
  // Booking subsystem (plan 07).
  booking_window_not_open: 422,
  booking_window_closed: 422,
  booking_capacity_full: 409,
  booking_already_exists: 409,
  booking_cancel_cutoff_passed: 409,
  session_cancelled: 409,
  waitlist_not_found: 404,
  pt_session_conflict: 409,
  pt_pack_required: 403,
  invoice_not_issued: 409,
};

/** Codes that are retriable by default (offline/retry logic relies on this). */
const RETRIABLE_BY_DEFAULT: ReadonlySet<ErrorCode> = new Set<ErrorCode>([
  'idempotency_in_progress',
  'fiscal_unavailable',
  'fiscal_failed_retriable',
  'rate_limited',
  'internal_error',
]);

/** True if `value` is one of the canonical error codes. */
export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && (ERROR_CODES as readonly string[]).includes(value);
}

/**
 * Canonical API error. Carries a stable `code`, derives a sensible HTTP status
 * and `retriable` default, and serializes to the locked `ErrorEnvelope`.
 */
export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly httpStatus: number;
  readonly retriable: boolean;
  readonly requestId: string;
  readonly details: Record<string, unknown> | undefined;

  constructor(code: ErrorCode, options: ApiErrorOptions = {}) {
    super(options.message ?? `errors.${code}`, { cause: options.cause });
    this.name = 'ApiError';
    this.code = code;
    this.httpStatus = options.httpStatus ?? DEFAULT_HTTP_STATUS[code];
    this.retriable = options.retriable ?? RETRIABLE_BY_DEFAULT.has(code);
    this.requestId = options.requestId ?? '';
    this.details = options.details;
  }

  /** Serialize to the locked error envelope. */
  toEnvelope(requestId?: string): ErrorEnvelope {
    const env: ErrorEnvelope['error'] = {
      code: this.code,
      message: this.message,
      request_id: requestId ?? this.requestId,
      retriable: this.retriable,
    };
    if (this.details !== undefined) {
      env.details = this.details;
    }
    return { error: env };
  }

  /** Narrowing helper for catch blocks. */
  static is(value: unknown): value is ApiError {
    return value instanceof ApiError;
  }
}

/** Build an `ErrorEnvelope` directly (e.g. from a non-ApiError throwable). */
export function toErrorEnvelope(code: ErrorCode, options: ApiErrorOptions = {}): ErrorEnvelope {
  return new ApiError(code, options).toEnvelope();
}
